import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw, Search } from "lucide-react";
import { mpAuditApi } from "./api";
import type { AuditLogItem, InspectPaymentResponse } from "./types";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field } from "../applications/shared/InfoBlock";
import { formatDateTime } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP } from "../dashboard/format";
import { useToast } from "@/app/providers/Toaster";

export function MpAuditScreen() {
  const [paymentId, setPaymentId] = useState("");
  const [result, setResult] = useState<InspectPaymentResponse | null>(null);
  const [rawOpen, setRawOpen] = useState(false);

  const { push } = useToast();

  const auditQuery = useQuery({
    queryKey: ["operations", "mp-audit", { limit: 20 }],
    queryFn: () => mpAuditApi.recent(20),
  });

  const inspect = useMutation({
    mutationFn: (id: string) => mpAuditApi.inspect(id),
    onSuccess: (res) => {
      setResult(res);
      push({ kind: "success", message: "Inspección OK. Resultado abajo." });
      auditQuery.refetch();
    },
    onError: (err: any) => {
      setResult(null);
      push({ kind: "error", message: err?.response?.data?.error || "No se pudo inspeccionar." });
    },
  });

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <header>
        <p className="nx-eyebrow mb-1">Operaciones · Auditoría MP</p>
        <h1 className="nx-h1">Auditoría MercadoPago</h1>
        <p className="text-sm text-fg-muted mt-1">
          Inspección manual de pagos por <code>payment_id</code>. Devuelve el
          JSON crudo del API MP + persiste un audit log con quién consultó qué.
        </p>
      </header>

      <section className="nx-card p-4">
        <h2 className="text-sm font-semibold text-fg mb-3">Inspeccionar pago</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="nx-eyebrow block mb-1">payment_id (numérico)</label>
            <input
              type="text"
              value={paymentId}
              onChange={(e) => setPaymentId(e.target.value.replace(/\D/g, ""))}
              placeholder="ej. 1234567890"
              className="nx-input"
            />
          </div>
          <button
            onClick={() => {
              if (!paymentId) {
                push({ kind: "error", message: "Ingresá un payment_id." });
                return;
              }
              inspect.mutate(paymentId);
            }}
            disabled={inspect.isPending}
            className="nx-btn-primary"
          >
            {inspect.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Inspeccionar
          </button>
        </div>

        {result && (
          <div className="mt-4 flex flex-col gap-4">
            <Section title="Resumen">
              <Field label="Payment ID" value={result.summary.paymentId} />
              <Field
                label="Estado"
                value={
                  <span className="inline-flex items-center gap-1">
                    <span
                      className={
                        result.summary.status === "approved"
                          ? "nx-badge-success"
                          : result.summary.status === "refunded"
                            ? "nx-badge-warning"
                            : "nx-badge-neutral"
                      }
                    >
                      {result.summary.status}
                    </span>
                    <span className="text-2xs text-fg-subtle">{result.summary.statusDetail}</span>
                  </span>
                }
              />
              <Field label="Monto" value={fmtCLP(result.summary.transactionAmount)} />
              <Field label="Recibido neto" value={fmtCLP(result.summary.netReceivedAmount)} />
              <Field
                label="Comisión MP"
                value={result.summary.feeMP !== null ? fmtCLP(result.summary.feeMP) : "—"}
              />
              <Field label="Moneda" value={result.summary.currency} />
              <Field label="Método pago" value={result.summary.paymentMethodId} />
              <Field label="Reembolsos" value={result.summary.refunds} />
              <Field label="Aprobado" value={formatDateTime(result.summary.dateApproved)} wide />
              <Field label="Creado" value={formatDateTime(result.summary.dateCreated)} wide />
              <Field label="External ref" value={result.summary.externalReference ?? "—"} wide />
              <Field label="Collector ID" value={result.summary.collectorId} />
              <Field label="Email pagador" value={result.summary.payerEmail ?? "—"} wide />
            </Section>

            <details
              open={rawOpen}
              onToggle={(e) => setRawOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer text-2xs text-fg-subtle hover:text-fg">
                {rawOpen ? "Ocultar JSON crudo" : "Ver JSON crudo del API MP"}
              </summary>
              <pre className="mt-2 p-3 bg-bg-subtle border border-border-subtle rounded text-2xs overflow-auto max-h-96">
                {JSON.stringify(result.raw, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="nx-h2">Audit log reciente</h2>
          <button
            onClick={() => auditQuery.refetch()}
            disabled={auditQuery.isFetching}
            className="nx-btn-secondary"
          >
            <RefreshCw size={14} className={auditQuery.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>

        {auditQuery.isLoading ? (
          <LoadingBlock label="Cargando audit log…" />
        ) : auditQuery.isError ? (
          <ErrorBlock onRetry={() => auditQuery.refetch()} />
        ) : (
          <ApplicationsTable<AuditLogItem>
            rows={auditQuery.data?.items ?? []}
            columns={auditColumns(setPaymentId)}
            page={1}
            pageSize={(auditQuery.data?.items ?? []).length || 1}
            total={(auditQuery.data?.items ?? []).length}
            onPageChange={() => {}}
            emptyLabel="Sin consultas registradas todavía."
          />
        )}
      </section>
    </div>
  );
}

function auditColumns(onSelectPaymentId: (id: string) => void): Column<AuditLogItem>[] {
  return [
    {
      key: "id",
      header: "ID",
      cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
      width: "min",
    },
    {
      key: "paymentId",
      header: "Payment ID",
      cell: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelectPaymentId(r.paymentId);
          }}
          className="text-accent hover:underline text-xs tabular-nums"
          title="Cargar arriba para re-inspeccionar"
        >
          {r.paymentId}
        </button>
      ),
    },
    {
      key: "status",
      header: "HTTP",
      cell: (r) => (
        <span
          className={
            r.responseStatus === 200
              ? "nx-badge-success"
              : r.responseStatus && r.responseStatus >= 400
                ? "nx-badge-danger"
                : "nx-badge-neutral"
          }
        >
          {r.responseStatus ?? "—"}
        </span>
      ),
      width: "min",
    },
    {
      key: "mpStatus",
      header: "Status MP",
      cell: (r) => <span className="text-fg-muted text-xs">{r.summary?.status ?? "—"}</span>,
      width: "min",
    },
    {
      key: "amount",
      header: "Monto",
      cell: (r) => (
        <span className="text-fg text-xs tabular-nums">
          {r.summary?.transactionAmount !== undefined
            ? fmtCLP(r.summary.transactionAmount)
            : "—"}
        </span>
      ),
      align: "right",
      width: "min",
    },
    {
      key: "extRef",
      header: "External ref",
      cell: (r) => (
        <span className="text-fg-subtle text-xs">{r.summary?.externalReference ?? "—"}</span>
      ),
    },
    {
      key: "by",
      header: "Solicitado por",
      cell: (r) => <span className="text-fg-subtle text-xs">{r.requestedBy ?? "—"}</span>,
    },
    {
      key: "when",
      header: "Cuándo",
      cell: (r) => <span className="text-fg-subtle text-xs">{formatDateTime(r.createdAt)}</span>,
      width: "min",
      align: "right",
    },
  ];
}
